/**
 * API Endpoints Module
 * Replaces: Hardcoded URLs in Form5.cs, AddinModule.cs
 * Centralizes all API endpoint definitions with proper typing
 */

import type { ApiResponse } from './httpClient';
import { HttpClient, HttpError, dataDeliveryClient, backgroundFetcherClient, istariClient, legacyClient,istariNewClient } from './httpClient';
import { createLogger } from '../../utils/logger';
import { devLog } from '../../utils/devLog';
import { config } from '../../utils/config';
import { TypedMemoryCache, MemoryCache } from '../cache/memoryCache';

const logger = createLogger('API');

/**
 * Several consensus endpoints answer a valid request that simply has no matching
 * rows with a 404 + { detail: "No … found …" } (e.g. query_uic_meta → "No data
 * found for the specified filters", query_broker_list → "No broker data found for
 * the specified ticker"). For data-lookup endpoints that is a NORMAL, expected
 * outcome — the ticker just has no line items / brokers — not a failure that
 * should abort the whole Insert flow. This coerces such 404s into a well-formed
 * EMPTY response so callers degrade gracefully (empty sheet / no brokers). Any
 * other error (auth, 5xx, network) still propagates.
 */
async function emptyOn404<T>(promise: Promise<ApiResponse<T>>, emptyData: T): Promise<ApiResponse<T>> {
  try {
    return await promise;
  } catch (error) {
    if (error instanceof HttpError && error.status === 404) {
      logger.warn('Endpoint returned 404 (no data) — treating as empty result', { status: error.status });
      return { data: emptyData, status: 404, headers: new Headers() };
    }
    throw error;
  }
}

// In-flight request dedup maps. If two callers ask for the same ticker
// before the first network response returns, both await the same promise.
const _inflightTickerInfo = new Map<string, Promise<ApiResponse<TickerInfoResponse>>>();
const _inflightBrokerList = new Map<string, Promise<ApiResponse<{ ANALYST: string[]; BROKER: string[]; TICKER: string[]; TICKER_ID: number[] }>>>();

// Broker list cache (30 min TTL). Keyed by ticker + usernameFilter.
const _brokerListCache = new MemoryCache<{ ANALYST: string[]; BROKER: string[]; TICKER: string[]; TICKER_ID: number[] }>({
  ttl: 30 * 60 * 1000,
  maxSize: 200,
});

// The consensus backend refreshes the ticker universe ~12:00 and ~17:00 IST; we
// re-fetch ~10 min later (12:10 / 17:10 IST) so the update has settled. Folding
// this INDIA-TIME window into the ticker-list cache key means each user fetches
// once per window (on their next request after the boundary) — no cron, purely
// request-driven, and correct regardless of the user's own timezone. The three
// daily windows are AM (prev-day 17:10 → 12:10), NOON (12:10 → 17:10), EVE (17:10 →).
function tickerDataEpoch(): string {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(new Date());
    const p: Record<string, string> = {};
    for (const part of parts) p[part.type] = part.value;
    const mins = parseInt(p.hour, 10) * 60 + parseInt(p.minute, 10);
    const bucket = mins >= 17 * 60 + 10 ? 'EVE' : mins >= 12 * 60 + 10 ? 'NOON' : 'AM';
    return `${p.year}-${p.month}-${p.day}-${bucket}`;
  } catch {
    // If Intl timezone data is unavailable, fall back to a UTC-day bucket so the
    // list still refreshes at least daily rather than being cached forever.
    return new Date().toISOString().slice(0, 10);
  }
}

// ============================================================================
// API Response Types
// ============================================================================

export interface TickerInfoResponse {
  TICKER: string[];
  COMPANY_NAME: string[];
  FROM_A: number[];
  FROM_C: number[];
  TO_A: number[];
  TO_C: number[];
  REPORTED_FISCALPERIOD: string[];
  BROKERCOUNT: number[];
  FYENDING: string[];
  CONSENSUS_UD: string[];
  TICKER_ID: number[];
  ACTUALS: string[];
  ALT_TICKER: string[];
  BLOOMBERG: string[];
  BLOOM_SUBINDUSTRY: string[];
  CIK: string[];
  COUNTRY: string[];
  CUSIP: string[];
  EXCHANGE: string[];
  EXCHANGE_COUNTRY: string[];
  EXCHANGE_NAME: string[];
  INDUSTRY: string[];
  INSYNC_SUBINDUSTRY: string[];
  IPO_DATE: string[];
  ISIN: string[];
  OLD_TICKER: string[];
  PRIMARY_CURRENCY: string[];
  REGION: string[];
  REUTERS: string[];
  SECONDARY_CURRENCY: string[];
  SECTOR: string[];
  SEDOL: string[];
  TICKER_STATUS: boolean[];
}

export interface TickerInfoAllResponse {
  TICKER: string[];
  COMPANY_NAME: string[];
}

// Alias for TickerInfoAllResponse used in autocomplete
export type TickerInfo = TickerInfoAllResponse;

export interface LineItemMeta {
  LINEITEM: string;
  LTCode: string;
  LineSequence: string;
  LineSequence_Int: number;
  ROWFORMAT: string;
  INDENT: string;
  CONSENSUS_UD: string;
  NUMBERFORMAT: string;
  COMPANYNAME: string;
  FYENDING: string;
  CATEGORY_CODE: 'IS' | 'BS' | 'CF' | 'OT' | 'KPI' | 'SD' | 'KM' | 'RT' | 'GD';
  KPISEQUENCE?: string;
  KPISEQUENCE_Int?: number;
}

export interface FiscalPeriodInfo {
  FiscalPeriod: string;
  FISCALPERIOD?: string;
  LAST_REPORTED_FISCALPERIOD?: string;
  FISCALYEARSEQUENCE?: string | number;
  EST_SOURCE: 'A' | 'E' | 'InSync Estimates';
  FiscalYearSequnce: number;
}

export interface BatchDataRequest {
  Key: string;
}

export interface BatchDataResponse {
  Key: string;
  Value: any;
}

export interface LegacyStockDataOption {
  InformationText?: string;
  InformationCode?: string;
  INFORMATIONTEXT?: string;
  INFORMATIONCODE?: string;
}

// ============================================================================
// Endpoints - Consensus API (data-deliveryv2.insyncanalytics.com)
// ============================================================================

export const consensusEndpoints = {
  /**
   * Get ticker information
   * Equivalent to: consensus/query_tickerinfo?ticker={ticker}
   * Original: Form5.cs Auto() method, line 113
   *
   * Caches per-ticker in TypedMemoryCache.tickerInfoCache (30 min TTL).
   * In-flight requests are deduped so concurrent callers share one network call.
   */
  async getTickerInfo(ticker: string): Promise<ApiResponse<TickerInfoResponse>> {
    const cacheKey = `tickerinfo_${ticker}`;
    const cached = TypedMemoryCache.tickerInfoCache.get(cacheKey);
    if (cached) {
      devLog(`[TIMING][API] query_tickerinfo CACHE HIT ticker=${ticker}`);
      return { data: cached, status: 200, headers: new Headers() };
    }
    const inflight = _inflightTickerInfo.get(ticker);
    if (inflight) {
      devLog(`[TIMING][API] query_tickerinfo INFLIGHT-DEDUP ticker=${ticker}`);
      return inflight;
    }

    const startTime = performance.now();
    devLog(`[TIMING][API] query_tickerinfo START ticker=${ticker}`);
    logger.apiRequest('GET', `consensus/query_tickerinfo?ticker=${ticker}`);
    const promise = dataDeliveryClient.get<TickerInfoResponse>(
      `consensus/query_tickerinfo?ticker=${encodeURIComponent(ticker)}`
    ).then(response => {
      const duration = performance.now() - startTime;
      devLog(`[TIMING][API] query_tickerinfo END ticker=${ticker} duration=${duration.toFixed(2)}ms`);
      logger.apiResponse('GET', 'consensus/query_tickerinfo', response.status, {
        ticker,
        durationMs: Math.round(duration),
      });
      if (response.data) {
        TypedMemoryCache.tickerInfoCache.set(cacheKey, response.data);
      }
      return response;
    }).finally(() => {
      _inflightTickerInfo.delete(ticker);
    });

    _inflightTickerInfo.set(ticker, promise);
    return promise;
  },

  /**
   * Get all ticker info for autocomplete (alias for getAllTickers)
   * Equivalent to: consensus/query_tickerinfo_all?username_filter={token}
   */
  async getAllTickerInfo(usernameFilter: string, includeCols: string = ''): Promise<ApiResponse<TickerInfoAllResponse>> {
    // Per-user key + IST data window: cache holds until the next 12:10/17:10 IST
    // refresh boundary, then the user's next request re-fetches the fresh list.
    const cacheKey = `tickerinfo_all_${usernameFilter}_${includeCols}_${tickerDataEpoch()}`;

    // Check cache first
    const cached = TypedMemoryCache.tickerInfoAllCache.get(cacheKey);
    if (cached) {
      // devLog('Cache hit for getAllTickerInfo', { usernameFilter });
      logger.debug('Cache hit for getAllTickerInfo', { usernameFilter });
      return { data: cached, status: 200, headers: new Headers() };
    }
    // Fetch from API
    const response = await dataDeliveryClient.get<TickerInfoAllResponse>(
      `consensus/query_tickerinfo_all?username_filter=${encodeURIComponent(usernameFilter)}${includeCols ? `&include_cols=${encodeURIComponent(includeCols)}` : ''}`
    );

    // Save to cache
    if (response.data) {
      TypedMemoryCache.tickerInfoAllCache.set(cacheKey, response.data);
      logger.debug('Cached getAllTickerInfo response', { usernameFilter });
      devLog('Cached getAllTickerInfo response', { usernameFilter });
    }

    return response;
  },

  /**
   * Get line item metadata (UIC = Universal Item Code)
   * Equivalent to: consensus/query_uic_meta?ticker={ticker}&catagory_code={categories}&is_inferred={bool}
   */
  async getLineItemMeta(
    ticker: string,
    includeInferred: boolean = false,
    categoryCodes: string[] = []
  ): Promise<ApiResponse<LineItemMeta[]>> {
    const params = new URLSearchParams({ ticker });

    if (categoryCodes.length > 0) {
      params.set('catagory_code', categoryCodes.join(','));
    }

    params.set('is_inferred', String(includeInferred));

    // A ticker with no line items for these filters returns 404 ("No data found
    // for the specified filters"); treat that as an empty result, not a failure.
    return emptyOn404(
      dataDeliveryClient.get<LineItemMeta[]>(`consensus/query_uic_meta?${params.toString()}`),
      [] as unknown as LineItemMeta[]
    );
  },

  /**
   * Get fiscal periods list
   * Equivalent to: consensus/list_fiscal_period?ticker={ticker}&from_year={from}&to_year={to}&include_quarter={bool}
   * Original: Form5.cs AverageConsensus() method, line 1595-1601
   */
  async getFiscalPeriods(
    ticker: string,
    fromYear: string | number,
    toYear: string | number,
    includeQuarter: boolean
  ): Promise<ApiResponse<FiscalPeriodInfo[]>> {
    return emptyOn404(
      dataDeliveryClient.get<FiscalPeriodInfo[]>(
        `consensus/list_fiscal_period?ticker=${encodeURIComponent(ticker)}` +
        `&from_year=${fromYear}&to_year=${toYear}&include_quarter=${includeQuarter}`
      ),
      [] as unknown as FiscalPeriodInfo[]
    );
  },

  async getFiscalPeriodsForTicker(ticker: string): Promise<ApiResponse<FiscalPeriodInfo[]>> {
    return emptyOn404(
      dataDeliveryClient.get<FiscalPeriodInfo[]>(
        `consensus/list_fiscal_period?ticker=${encodeURIComponent(ticker)}`
      ),
      [] as unknown as FiscalPeriodInfo[]
    );
  },

  /**
   * Get broker list for ticker
   * Equivalent to: consensus/query_broker_list?ticker={ticker}&username_filter={token}
   * Original: Form5.cs LoadBrokerList() method, lines 400-445
   */
  async getBrokerList(
    ticker: string,
    usernameFilter: string
  ): Promise<ApiResponse<{ ANALYST: string[]; BROKER: string[]; TICKER: string[]; TICKER_ID: number[] }>> {
    const cacheKey = `brokerlist_${ticker}_${usernameFilter}`;
    const cached = _brokerListCache.get(cacheKey);
    if (cached) {
      devLog(`[TIMING][API] query_broker_list CACHE HIT ticker=${ticker}`);
      return { data: cached, status: 200, headers: new Headers() };
    }
    const inflight = _inflightBrokerList.get(cacheKey);
    if (inflight) {
      devLog(`[TIMING][API] query_broker_list INFLIGHT-DEDUP ticker=${ticker}`);
      return inflight;
    }

    const startTime = performance.now();
    devLog(`[TIMING][API] query_broker_list START ticker=${ticker}`);
    // A ticker with no brokers returns 404 ("No broker data found for the specified
    // ticker"); treat that as an empty broker list so the insert proceeds without
    // broker columns instead of failing.
    const promise = emptyOn404(
      dataDeliveryClient.get<{ ANALYST: string[]; BROKER: string[]; TICKER: string[]; TICKER_ID: number[] }>(
        `consensus/query_broker_list?ticker=${encodeURIComponent(ticker)}&username_filter=${encodeURIComponent(usernameFilter)}&include_cols=BROKER,TICKER_ID,TICKER,ANALYST`
      ),
      { ANALYST: [], BROKER: [], TICKER: [], TICKER_ID: [] }
    ).then(response => {
      const duration = performance.now() - startTime;
      devLog(`[TIMING][API] query_broker_list END ticker=${ticker} duration=${duration.toFixed(2)}ms`);
      // Only cache a real, non-empty result — never the coerced 404 empty, so a
      // later retry can still fetch brokers if the backend starts returning them.
      if (response.status === 200 && response.data?.BROKER?.length) {
        _brokerListCache.set(cacheKey, response.data);
      }
      return response;
    }).finally(() => {
      _inflightBrokerList.delete(cacheKey);
    });

    _inflightBrokerList.set(cacheKey, promise);
    return promise;
  },

  async getGuidanceData(
    ticker: string,
    usernameFilter: string,
    useNewGuidance: boolean = false
  ): Promise<ApiResponse<any[] | Record<string, any[]>>> {
    // Mirrors AddinModule.btnGenerateGuidance: the new guidance flow pulls from
    // guidance_with_actuals_estimates_deltas, the legacy flow from guidance_fetch_data.
    const guidanceEndpoint = useNewGuidance
      ? 'consensus/guidance_with_actuals_estimates_deltas'
      : 'consensus/guidance_fetch_data';
    return dataDeliveryClient.get<any[] | Record<string, any[]>>(
      `${guidanceEndpoint}?ticker=${encodeURIComponent(ticker)}&username_filter=${encodeURIComponent(usernameFilter)}`
    );
  },

  /**
   * Get broker-specific estimates/actuals data.
   * Equivalent to: consensus/query_estimates_actuals_broker?ticker={ticker}&username_filter={token}&exclude_cols={...}
   */
  async getBrokerEstimatesActuals(
    ticker: string,
    usernameFilter: string,
    excludeCols: string = '',
    fiscalPeriod: string = '',
    ltCode: string = ''
  ): Promise<ApiResponse<any>> {
    devLog('getBrokerEstimatesActuals', ticker, usernameFilter, excludeCols, fiscalPeriod, ltCode);
    const params = new URLSearchParams({
      ticker,
      username_filter: usernameFilter,
    });
    if (excludeCols) params.set('exclude_cols', excludeCols);
    if (fiscalPeriod) params.set('fiscalperiod', fiscalPeriod);
    if (ltCode) params.set('ltcode', ltCode);

    return dataDeliveryClient.get<any>(
      `consensus/query_estimates_actuals_broker?${params.toString()}`
    );
  },

  async getAuditData(
    ticker: string,
    usernameFilter: string,
    fiscalPeriod: string = ''
  ): Promise<ApiResponse<any[] | Record<string, any[]>>> {
    const params = new URLSearchParams({
      ticker,
      username_filter: usernameFilter,
    });
    if (fiscalPeriod) params.set('fiscalperiod', fiscalPeriod);

    return dataDeliveryClient.get<any[] | Record<string, any[]>>(
      `consensus/get_audit_data?${params.toString()}`
    );
  },

  async invalidateTickerCache(
    ticker: string,
    usernameFilter: string
  ): Promise<ApiResponse<any>> {
    const params = new URLSearchParams({
      ticker,
      username_filter: usernameFilter,
    });

    return dataDeliveryClient.post<any>(
      `consensus_v2/invalidate_ticker_cache?${params.toString()}`,
      { body: {} }
    );
  },

  async getValuationMetrics(): Promise<ApiResponse<{ metrics: string[] }>> {
    return dataDeliveryClient.get<{ metrics: string[] }>('consensus/metrics');
  },

  /**
   * Historical share price from the consensus backend.
   * `consensus/historical_share_price?ticker={ticker}`
   *
   * Replaces the legacy `legacy.getHistoricalSharePrice`. Differences the caller
   * must handle (see stockPricesService.extractHistoricalRows):
   *   - rows are NESTED under `.historical`, not at the top level
   *   - values are real numbers, not numeric strings
   *   - no TokenID/username_filter needed
   * Selected by the USE_NEW_HISTORICAL_API flag.
   */
  async getHistoricalSharePriceV2(ticker: string): Promise<ApiResponse<any>> {
    // A ticker with no price history answers 404; that is an empty result.
    return emptyOn404(
      dataDeliveryClient.get<any>(
        `consensus/historical_share_price?ticker=${encodeURIComponent(ticker)}`
      ),
      []
    );
  },

  async getStockSplit(ticker: string): Promise<ApiResponse<any[] | Record<string, any[]>>> {
    // A ticker with no splits answers 404 + {detail: "No data found for the
    // specified filters"}. That is an empty result, not a failure — coerce it so
    // the pane shows "No stock split data…" rather than a raw "HTTP 404:".
    return emptyOn404(
      dataDeliveryClient.get<any[] | Record<string, any[]>>(
        `consensus/query_stock_split?ticker=${encodeURIComponent(ticker)}`
      ),
      []
    );
  },

  async getValuation(ticker: string, mode: string): Promise<ApiResponse<any[] | Record<string, any[]>>> {
    // See getStockSplit: 404 here means "no valuation rows", not an error.
    return emptyOn404(
      dataDeliveryClient.get<any[] | Record<string, any[]>>(
        `consensus/valuation?ticker=${encodeURIComponent(ticker)}&mode=${encodeURIComponent(mode)}`
      ),
      []
    );
  }
};

// ============================================================================
// Endpoints - Batch Data API (Background Fetcher)
// ============================================================================

export const batchEndpoints = {
  /**
   * Get batch data for custom functions
   * Equivalent to: consensus_v2/get_add_in_data (POST)
   * Original: BackgroundFetcher.cs FetchBatchAsync() method, line 230
   * 
   * This is the core API for IAD/IBD custom functions
   */
  async getBatchData(
    keys: string[],
    usernameFilter: string
  ): Promise<ApiResponse<BatchDataResponse[]>> {
    const payload: BatchDataRequest[] = keys.map(k => ({ Key: k }));
    
    return backgroundFetcherClient.post<BatchDataResponse[]>(
      `consensus_v2/get_add_in_data?username_filter=${encodeURIComponent(usernameFilter)}`,
      {
        body: payload,
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
  }
};

// ============================================================================
// Endpoints - Legacy API (insyncanalytics.asia)
// ============================================================================

// These endpoints are for features not yet migrated to V2 API
export const legacyEndpoints = {
  /**
   * Login validation (Legacy - GET method)
   * Equivalent to: api/LoginDetails?Uname={user}&Passw={pass}
   * Original: AddinModule.cs adxRButtonLogin_OnClick() method, line 404
   */
  async validateLogin(username: string, password: string): Promise<ApiResponse<any>> {
    return dataDeliveryClient.get(
      `api/LoginDetails?Uname=${encodeURIComponent(username)}&Passw=${encodeURIComponent(password)}`
    );
  },

  /**
   * Login validation (New V2 - POST method with additional parameters)
   * Endpoint: addin/login/
   * Base URL: https://istarinew.insyncanalytics.in/
   */
  async validateLoginV2(
    username: string,
    password: string,
    sysname: string,
    mcid: string,
    versionNo: string,
    // OTP step for accounts the server flags with `is_verify`: the same login
    // body is re-sent with the emailed code. Omitted entirely on a normal login.
    verificationCode?: string,
    /**
     * Send to the istarinew host instead of the default istari one. Dev-only
     * escape hatch used by the "Login with Istari New" button; the request body
     * and the response handling are identical, only the base URL differs.
     */
    useIstariNew = false
  ): Promise<ApiResponse<any>> {
    devLog('validateLoginV2', { username, sysname, mcid, versionNo, hasVerificationCode: !!verificationCode, useIstariNew });
    // Attribute the login request to the submitted user (storage isn't populated
    // yet pre-auth), so login shows up per-user in the nginx access log too.
    return (useIstariNew ? istariNewClient : istariClient).post('addin/login/', {
      // Login authenticates via the body; no Basic credential (VAPT V-001). The
      // X-Insync-User header lets nginx attribute the request and seed the minted
      // token's subject.
      skipAuth: true,
      headers: username
        ? { 'X-Insync-User': username.replace(/[\r\n]/g, '').slice(0, 320) }
        : undefined,
      body: {
        UserName: username,
        Password: password,
        sysname: sysname,
        MCID: mcid,
        VersionNo: versionNo,
        ...(verificationCode ? { VerificationCode: verificationCode } : {}),
      }
    });
  },

  /**
   * Logout (New V2 - POST method)
   * Endpoint: addin/logout/
   * Base URL: https://istarinew.insyncanalytics.in/ (same client as addin/login/)
   * Original: ADXExcelTaskPane1.cs btnLogout_Click() method, line 575
   * POSTs the machine id + token so the server can invalidate the session/device.
   */
  async logoutV2(tokenKey: string, mcid: string, useIstariNew = false): Promise<ApiResponse<any>> {
    // Must match the host the session was created on, or the server has no
    // record of the device to invalidate.
    return (useIstariNew ? istariNewClient : istariClient).post('addin/logout/', {
      skipAuth: true, // VAPT V-001: no Basic credential in the browser
      body: {
        MCID: mcid,
        TokenKey: tokenKey,
      },
    });
  },

  /**
   * Forgot password
   * Equivalent to: api/ForgotPassword?EmailID={email}
   * Original: AddinModule.cs adxRButtonForgotPass_OnClick() method, line 556
   */
  async forgotPassword(email: string): Promise<ApiResponse<any>> {
    return dataDeliveryClient.get(
      `api/ForgotPassword?EmailID=${encodeURIComponent(email)}`
    );
  },

  /**
   * Stock data list used by Insert Formula stock tab.
   * Equivalent to: api/FetchListStockData
   * Original: InsertFormulaForm.cs stock comboBox7 load.
   */
  async getStockDataList(): Promise<ApiResponse<LegacyStockDataOption[] | Record<string, any[]>>> {
    return legacyClient.get<LegacyStockDataOption[] | Record<string, any[]>>(
      'api/FetchListStockData',
      { skipAuth: true }
    );
  },

  async getHistoricalSharePrice(ticker: string, tokenId: string): Promise<ApiResponse<any[] | Record<string, any[]>>> {
    // See getStockSplit: 404 here means "no price history", not an error.
    return emptyOn404(
      legacyClient.get<any[] | Record<string, any[]>>(
        `api/HistoricalSharePrice?TickerName=${encodeURIComponent(ticker)}&TokenID=${encodeURIComponent(tokenId)}`,
        { skipAuth: true }
      ),
      []
    );
  },

  
  /**
   *
   * @param direct  false (default) → same-origin via the /legacy-api proxy, like
   *                every other call. true → a DIRECT cross-origin call straight to
   *                insyncanalytics.asia (API_BASE_URL), replicating Postman exactly
   *                to diagnose whether the proxy alters the request. Note: a
   *                cross-origin GET with an Authorization header triggers a CORS
   *                preflight, so the host must allow it or the browser blocks it.
   */
  async encryptUserToken(username: string, password: string, direct = false): Promise<ApiResponse<any>> {
    if (direct) {
      // Hit the API host directly with NO options — creds as the client's default
      // Basic auth, no custom headers (mirrors the proxy branch below). Cross-origin,
      // so subject to CORS.
      const directClient = new HttpClient(
        config.API_BASE_URL || 'https://insyncanalytics.asia',
        username,
        password,
        { timeout: 10 * 60 * 1000 }
      );
      return directClient.get<any>('api/v3/InSyncAuthenticationEncrypt');
    }

    const proxyClient = new HttpClient(
      config.LEGACY_PROXY_URL,
      username,
      password,
      { timeout: 10 * 60 * 1000}
    );
    // This IS the login step, so the user is not yet in tokenStorage — the shared
    // resolveInsyncUser() returns null here and no X-Insync-User header is attached,
    // leaving the encrypt call unattributed in Loki (`user_email`). We already know
    // the identity (the `username` arg), so attach it explicitly. Same-origin only
    // (the proxy branch), sanitized for header injection. The auto-attach guard in
    // HttpClient skips when the header is already present, so this is not overwritten.
    const insyncUser = username.replace(/[\r\n]/g, '').slice(0, 320);
    return proxyClient.get<any>('api/v3/InSyncAuthenticationEncrypt', {
      headers: insyncUser ? { 'X-Insync-User': insyncUser } : {},
    });
  }
};


export const api = {
  consensus: consensusEndpoints,
  batch: batchEndpoints,
  legacy: legacyEndpoints
};

export default api;
